/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '../../components/core';
import {
  AUTOMATION_TOKEN_LIFETIME_MONTHS,
  buildTokenExpiryDate,
  buttonPaddingSx,
  describeTokenExpiry,
  getApiErrorMessage,
} from './utils';
import { DialogCloseButton } from '../../components/core/DialogCloseButton';
import { useCreateCredentialMutation } from '../../store/modules/merchant/api';
import type {
  MerchantExternalCredential,
  ShopifyCredentialConfiguration,
} from '../../store/modules/merchant/types';
import { toast } from '../../util/toast';

/**
 * The automation token is only mandatory the first time. Shopify shows it once, so an admin
 * rotating just the client secret should not be forced to generate a new one: leaving the field
 * blank keeps whatever is already stored.
 */
const buildSchema = (isTokenRequired: boolean) =>
  z.object({
    clientId: z.string().trim().min(1, 'Client ID is required'),
    clientSecret: z.string().trim().min(1, 'Client secret is required'),
    appAutomationToken: isTokenRequired
      ? z.string().trim().min(1, 'Automation token is required')
      : z.string().trim(),
  });

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

type Props = {
  isOpen: boolean;
  merchantId: string;
  shopDomain: string;
  existingCredential?: MerchantExternalCredential;
  onClose: () => void;
  /** Receives the token's expiry when a new token was entered, so the caller can record it. */
  onSaved: (appAutomationTokenExpiresAt?: string) => Promise<void>;
};

export const ShopifyCredentialDialog = ({
  isOpen,
  merchantId,
  shopDomain,
  existingCredential,
  onClose,
  onSaved,
}: Props) => {
  const [createCredential, { isLoading }] = useCreateCredentialMutation();

  const existingConfiguration = (
    existingCredential?.data as
      { configuration?: Partial<ShopifyCredentialConfiguration> } | undefined
  )?.configuration;

  const hasExistingToken = Boolean(existingConfiguration?.appAutomationToken);
  const hasPartnerKeys = Boolean(existingConfiguration?.clientId);
  const existingExpiry = describeTokenExpiry(existingConfiguration?.appAutomationTokenExpiresAt);

  const schema = useMemo(() => buildSchema(!hasExistingToken), [hasExistingToken]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { clientId: '', clientSecret: '', appAutomationToken: '' },
  });

  useEffect(() => {
    if (isOpen) {
      reset({ clientId: '', clientSecret: '', appAutomationToken: '' });
    }
  }, [isOpen, reset]);

  const close = () => {
    reset();
    onClose();
  };

  const submit = async ({ clientId, clientSecret, appAutomationToken }: FormValues) => {
    const configuration: ShopifyCredentialConfiguration = {
      ...existingConfiguration,
      shopUrl: existingConfiguration?.shopUrl ?? `https://${shopDomain}`,
      token: existingConfiguration?.token ?? 'oauth',
      apiKey: existingConfiguration?.apiKey ?? 'oauth',
      apiSecretKey: clientSecret,
      clientId,
      clientSecret,
      // Only overwrite the stored token when a new one was entered, so a blank field on rotation
      // keeps the existing token and its expiry untouched.
      ...(appAutomationToken
        ? {
            appAutomationToken,
            appAutomationTokenExpiresAt: buildTokenExpiryDate(),
          }
        : {}),
    };

    try {
      await createCredential({
        merchantId,
        credentialType: 'ecommerce',
        systemOfOrigin: 'shopify',
        data: {
          externalId: shopDomain,
          apiVersion: '2026-07',
          configuration,
        },
      }).unwrap();
      await onSaved(configuration.appAutomationTokenExpiresAt);
      toast.success('Shopify Partner keys saved.');
      close();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to save Shopify Partner keys.'));
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogCloseButton onClose={close} />
      <DialogTitle sx={{ fontWeight: 600 }}>
        {hasPartnerKeys ? 'Rotate Shopify Partner keys' : 'Paste Shopify Partner keys'}
      </DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          noValidate
          spacing={2.5}
          sx={{ pt: 1 }}
          onSubmit={handleSubmit(submit)}
        >
          <TextField
            label="Shop domain"
            value={shopDomain}
            disabled
            helperText="The shop domain is locked after queue entry."
          />
          <TextField
            {...register('clientId')}
            label="Client ID"
            required
            error={!!errors.clientId}
            helperText={errors.clientId?.message}
          />
          <TextField
            {...register('clientSecret')}
            label="Client secret"
            type="password"
            required
            error={!!errors.clientSecret}
            helperText={errors.clientSecret?.message}
          />
          <TextField
            {...register('appAutomationToken')}
            label="App Automation Token"
            type="password"
            required={!hasExistingToken}
            error={!!errors.appAutomationToken}
            helperText={
              errors.appAutomationToken?.message ??
              (hasExistingToken
                ? `Leave blank to keep the stored token. ${existingExpiry?.label ?? ''}`.trim()
                : `Dev Dashboard, app Settings, App Automation Token. Choose the ${AUTOMATION_TOKEN_LIFETIME_MONTHS} month expiration; Shopify shows the token only once.`)
            }
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1.5} alignItems="center">
            <Button
              text="Cancel"
              theme="secondary"
              variant="outlined"
              sx={buttonPaddingSx}
              onClick={close}
              disabled={isLoading}
            />
            <Button text="Save keys" type="submit" loading={isLoading} sx={buttonPaddingSx} />
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
